import { type Currency } from '@principle-theorem/accounting';
import {
  TypeGuard,
  isDocRef,
  isINamedDocument,
  isObject,
  isSerialisedDocumentReference,
  isSerialisedTimestamp,
  isTimestamp,
  type DocumentReference,
  type SerialisedData,
  type Timestamp,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import type { IAppointmentSuggestion } from '../appointment/appointment-suggestion';
import type { IBrand } from '../brand';
import type { ITreatmentTemplate } from '../clinical-charting/treatment/treatment-template';
import type { IPractice } from '../practice/practice';
import type { IDepositPaymentAttempt } from './deposit-payment-attempt';
import { DepositPaymentMode } from './deposit-payment-mode';
import { DepositRequestStatus } from './deposit-request';

export enum PaymentStatus {
  Loading = 'loading',
  Pending = 'pending',
  Ready = 'ready',
  Processing = 'processing',
  Success = 'success',
  AlreadyPaid = 'alreadyPaid',
  Error = 'error',
  Expired = 'expired',
}

/**
 * Sent in the `details` of the error that refuses payment on an expired link,
 * so the payment page shows the link as expired rather than as a failed
 * payment.
 */
export const DEPOSIT_PAYMENT_LINK_EXPIRED_CODE = 'deposit-payment-link-expired';

export function isDepositPaymentLinkExpiredError(error: unknown): boolean {
  return (
    isObject(error) &&
    isObject(error.details) &&
    error.details.code === DEPOSIT_PAYMENT_LINK_EXPIRED_CODE
  );
}

export interface IDepositRequestData {
  practiceName: string;
  treatmentName: string;
  amountCents: number;
  currency: Currency;
  depositRequestId: string;
  /**
   * The brand's configured cancellation policy URL, surfaced so the payment
   * page can disclose the forfeiture policy before the card is charged. Absent
   * when the practice has not configured one.
   */
  cancellationPolicyUrl?: string;
}

export interface IGetDepositRequestParams {
  practiceRef: string;
  depositRequestId: string;
}

export interface IDepositRequestPendingResponse {
  status: PaymentStatus.Pending;
  depositRequestId: string;
  practiceName: string;
  treatmentName: string;
  amountCents: number;
  currency: Currency;
  connectedAccountId: string;
  /**
   * The brand's configured cancellation policy URL. Omitted when the practice
   * has not configured one so the page can fall back to plain disclosure copy
   * rather than rendering a dead link.
   */
  cancellationPolicyUrl?: string;
}

export interface IDepositRequestPaidResponse {
  status: PaymentStatus.AlreadyPaid;
  depositRequestId: string;
}

export interface IDepositRequestExpiredResponse {
  status: PaymentStatus.Expired;
  depositRequestId: string;
}

interface IGetDepositRequestResponseByStatus {
  [DepositRequestStatus.Pending]: IDepositRequestPendingResponse;
  [DepositRequestStatus.Authorised]: IDepositRequestPaidResponse;
  [DepositRequestStatus.Paid]: IDepositRequestPaidResponse;
  [DepositRequestStatus.Expired]: IDepositRequestExpiredResponse;
  [DepositRequestStatus.Cancelled]: IDepositRequestExpiredResponse;
  [DepositRequestStatus.Released]: IDepositRequestExpiredResponse;
  [DepositRequestStatus.Refunded]: IDepositRequestExpiredResponse;
}

export type IGetDepositRequestResponse =
  IGetDepositRequestResponseByStatus[DepositRequestStatus];

export interface ICreateOnlineBookingDepositPaymentIntentRequest {
  practiceRef: DocumentReference<IPractice>;
  brandRef: DocumentReference<IBrand>;
  treatmentTemplateRef: DocumentReference<ITreatmentTemplate>;
  appointmentStart: Timestamp;
  practitionerRef?: IAppointmentSuggestion['practitioner'];
  /**
   * The patient's checkout session. One PaymentIntent per session, as Stripe
   * recommends, so two patients on the same slot never share a payment and a
   * finished booking never blocks the next. Optional only so a client that
   * predates sessions keeps working through a deploy.
   */
  bookingSessionId?: string;
}

export interface ICreateOnlineBookingDepositPaymentIntentResponse {
  paymentAttemptRef: DocumentReference<IDepositPaymentAttempt>;
  clientSecret: string;
  amountCents: number;
  currency: Currency;
  paymentMode: DepositPaymentMode;
  connectedAccountId: string;
}

/**
 * Asks the server what deposit a booking would need. The portal runs as an
 * anonymous patient with no Firestore access to the brand, so every input to
 * the deposit decision has to be resolved server-side. `appointmentStart` and
 * `practitionerRef` are optional because the preview is shown from the moment
 * a treatment is picked, before any slot exists.
 */
export interface IResolveOnlineBookingDepositRequest {
  practiceRef: DocumentReference<IPractice>;
  brandRef: DocumentReference<IBrand>;
  treatmentTemplateRef: DocumentReference<ITreatmentTemplate>;
  appointmentStart?: Timestamp;
  practitionerRef?: IAppointmentSuggestion['practitioner'];
}

export const isGetDepositRequestParams =
  TypeGuard.interface<IGetDepositRequestParams>({
    practiceRef: isString,
    depositRequestId: isString,
  });

function isCanonicalBrandPath(path: string): boolean {
  const segments = path.split('/');
  return (
    segments.length === 4 &&
    segments[0] === 'organisations' &&
    Boolean(segments[1]) &&
    segments[2] === 'brands' &&
    Boolean(segments[3])
  );
}

function isCanonicalPracticePath(path: string): boolean {
  const segments = path.split('/');
  return (
    segments.length === 6 &&
    isCanonicalBrandPath(segments.slice(0, 4).join('/')) &&
    segments[4] === 'practices' &&
    Boolean(segments[5])
  );
}

function practiceBelongsToBrand(
  practicePath: string,
  brandPath: string
): boolean {
  return (
    isCanonicalPracticePath(practicePath) &&
    isCanonicalBrandPath(brandPath) &&
    practicePath.startsWith(`${brandPath}/practices/`)
  );
}

function isTreatmentTemplateReference(
  data: unknown
): data is DocumentReference<ITreatmentTemplate> {
  return isDocRef(data) && data.path.includes('/treatmentTemplates/');
}

function isSerialisedTreatmentTemplateReference(
  data: unknown
): data is SerialisedData<DocumentReference<ITreatmentTemplate>> {
  return (
    isSerialisedDocumentReference(data) &&
    data.referenceValue.includes('/treatmentTemplates/')
  );
}

function isPracticeReference(
  data: unknown
): data is DocumentReference<IPractice> {
  return isDocRef(data) && isCanonicalPracticePath(data.path);
}

function isBrandReference(data: unknown): data is DocumentReference<IBrand> {
  return isDocRef(data) && isCanonicalBrandPath(data.path);
}

function isSerialisedPracticeReference(
  data: unknown
): data is SerialisedData<DocumentReference<IPractice>> {
  return (
    isSerialisedDocumentReference(data) &&
    isCanonicalPracticePath(data.referenceValue)
  );
}

function isSerialisedBrandReference(
  data: unknown
): data is SerialisedData<DocumentReference<IBrand>> {
  return (
    isSerialisedDocumentReference(data) &&
    isCanonicalBrandPath(data.referenceValue)
  );
}

const isSerialisedOnlineBookingDepositPaymentIntentRequest =
  TypeGuard.interface<
    SerialisedData<ICreateOnlineBookingDepositPaymentIntentRequest>
  >({
    practiceRef: isSerialisedPracticeReference,
    brandRef: isSerialisedBrandReference,
    treatmentTemplateRef: isSerialisedTreatmentTemplateReference,
    appointmentStart: isSerialisedTimestamp,
    practitionerRef: TypeGuard.nilOr(
      TypeGuard.interface({
        ref: isSerialisedDocumentReference,
        name: isString,
      })
    ),
    bookingSessionId: TypeGuard.nilOr(isString),
  });

const isOnlineBookingDepositPaymentIntentRequest =
  TypeGuard.interface<ICreateOnlineBookingDepositPaymentIntentRequest>({
    practiceRef: isPracticeReference,
    brandRef: isBrandReference,
    treatmentTemplateRef: isTreatmentTemplateReference,
    appointmentStart: isTimestamp,
    practitionerRef: TypeGuard.nilOr(isINamedDocument),
    bookingSessionId: TypeGuard.nilOr(isString),
  });

export function isSerialisedCreateOnlineBookingDepositPaymentIntentRequest(
  data: unknown
): data is SerialisedData<ICreateOnlineBookingDepositPaymentIntentRequest> {
  if (!isSerialisedOnlineBookingDepositPaymentIntentRequest(data)) {
    return false;
  }

  return (
    practiceBelongsToBrand(
      data.practiceRef.referenceValue,
      data.brandRef.referenceValue
    ) &&
    data.treatmentTemplateRef.referenceValue.startsWith(
      `${data.brandRef.referenceValue}/treatmentTemplates/`
    )
  );
}

export function isCreateOnlineBookingDepositPaymentIntentRequest(
  data: unknown
): data is ICreateOnlineBookingDepositPaymentIntentRequest {
  if (!isOnlineBookingDepositPaymentIntentRequest(data)) {
    return false;
  }

  return (
    practiceBelongsToBrand(data.practiceRef.path, data.brandRef.path) &&
    data.treatmentTemplateRef.path.startsWith(
      `${data.brandRef.path}/treatmentTemplates/`
    )
  );
}

const isSerialisedResolveOnlineBookingDepositRequestShape = TypeGuard.interface<
  SerialisedData<IResolveOnlineBookingDepositRequest>
>({
  practiceRef: isSerialisedPracticeReference,
  brandRef: isSerialisedBrandReference,
  treatmentTemplateRef: isSerialisedTreatmentTemplateReference,
  appointmentStart: TypeGuard.nilOr(isSerialisedTimestamp),
  practitionerRef: TypeGuard.nilOr(
    TypeGuard.interface({
      ref: isSerialisedDocumentReference,
      name: isString,
    })
  ),
});

const isResolveOnlineBookingDepositRequestShape =
  TypeGuard.interface<IResolveOnlineBookingDepositRequest>({
    practiceRef: isPracticeReference,
    brandRef: isBrandReference,
    treatmentTemplateRef: isTreatmentTemplateReference,
    appointmentStart: TypeGuard.nilOr(isTimestamp),
    practitionerRef: TypeGuard.nilOr(isINamedDocument),
  });

export function isSerialisedResolveOnlineBookingDepositRequest(
  data: unknown
): data is SerialisedData<IResolveOnlineBookingDepositRequest> {
  if (!isSerialisedResolveOnlineBookingDepositRequestShape(data)) {
    return false;
  }

  return (
    practiceBelongsToBrand(
      data.practiceRef.referenceValue,
      data.brandRef.referenceValue
    ) &&
    data.treatmentTemplateRef.referenceValue.startsWith(
      `${data.brandRef.referenceValue}/treatmentTemplates/`
    )
  );
}

export function isResolveOnlineBookingDepositRequest(
  data: unknown
): data is IResolveOnlineBookingDepositRequest {
  if (!isResolveOnlineBookingDepositRequestShape(data)) {
    return false;
  }

  return (
    practiceBelongsToBrand(data.practiceRef.path, data.brandRef.path) &&
    data.treatmentTemplateRef.path.startsWith(
      `${data.brandRef.path}/treatmentTemplates/`
    )
  );
}
